import * as Sentry from "@sentry/capacitor";
import * as React from "react";
import { API } from "../API";
import { openScanner, ScanConfiguration } from "../components/MedflytScanner";
import { RecruitmentFormScreen } from "../components/RecruitmentFormScreen";
import { isLoading, Loading, loading } from "../Loading";
import { RecruitmentCaregiverApplicationQuestionId } from "../messages/ObjectIds";
import { RecruitmentQuestionData } from "../messages/Recruitment";
import { OrientationLanguages } from "../messages/Video";
import { AppCaregiverState } from "../system/AppState/AppCaregiverState";
import { getLanguage, langMap } from "../translate";

interface RecruitmentFormActivityProps {
  api: API;
  onBackClick: () => void;
  onSubmitted: () => void;
  caregiver: AppCaregiverState | Loading;
}

interface RecruitmentFormActivityState {
  language: OrientationLanguages | null;
  recruitmentForm: RecruitmentQuestionData[] | Loading;
  waitingForSubmit: boolean;
  showSubmitModal: boolean;
  showMandatoryProblems: boolean;
}

export class RecruitmentFormActivity extends React.Component<
  RecruitmentFormActivityProps,
  RecruitmentFormActivityState
> {
  constructor(props: RecruitmentFormActivityProps) {
    super(props);

    this.state = {
      language: "English",
      recruitmentForm: loading(),
      waitingForSubmit: false,
      showSubmitModal: false,
      showMandatoryProblems: false,
    };

    this.asyncLoadLanguage();
  }

  asyncLoadLanguage() {
    getLanguage().then((language) => {
      this.setState({
        language: langMap[language],
      });
    });

    this.props.api.getRecruitmentFormInfo().then((questions) => {
      this.setState({
        recruitmentForm: questions.applicationData === null ? [] : questions.applicationData,
      });
    });
  }

  getQuestionById(id: RecruitmentCaregiverApplicationQuestionId) {
    const questionId = RecruitmentCaregiverApplicationQuestionId.unwrap(id);
    if (isLoading(this.state.recruitmentForm)) {
      throw new Error("wtf");
    }
    const question = this.state.recruitmentForm.filter((val) => {
      return RecruitmentCaregiverApplicationQuestionId.unwrap(val.id) === questionId;
    });
    if (question.length !== 1) {
      throw new Error(`Couldn't find element ${questionId} in questions form`);
    }
    return question[0];
  }

  handleAnswer = (
    id: RecruitmentCaregiverApplicationQuestionId,
    answer: string | number | number[] | null
  ) => {
    this.props.api.postRecruitmentAnswer(id, answer);
    const question = this.getQuestionById(id);
    question.answer = answer;
  };

  scanSuccessCallback = (
    resultBase: string | undefined,
    questionId: RecruitmentCaregiverApplicationQuestionId
  ) => {
    (window as any).resolveLocalFileSystemURL(resultBase, (data: any) => {
      const image = document.getElementById(
        `myImage-${RecruitmentCaregiverApplicationQuestionId.unwrap(questionId)}`
      ) as HTMLImageElement;
      image.onload = () => {
        const canvas = document.createElement(`CANVAS`) as HTMLCanvasElement;
        const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
        canvas.height = image.naturalHeight;
        canvas.width = image.naturalWidth;
        ctx.canvas.height = image.naturalHeight;
        ctx.canvas.width = image.naturalWidth;
        ctx.drawImage(image, 0, 0);
        let dataurl: string = "";
        dataurl = canvas.toDataURL("image/jpeg", 0.7);
        this.props.api.uploadRecruitmentFile(questionId, dataurl);
        this.forceUpdate();
      };
      const question = this.getQuestionById(questionId);
      const finalImgURI = data.toInternalURL();
      question.answer = finalImgURI;
      image.src = finalImgURI; // For iOS, use image.src = imageURI + '?' + Date.now(); to solve issue 10
    });
  };

  scanErrorCallback = (message: string) => {
    Sentry.captureException(new Error(`Error on scanning recruitment form document: ${message}`));
  };

  handlePicUpload = (questionId: RecruitmentCaregiverApplicationQuestionId) => {
    const cordovaScannerOptions: ScanConfiguration = {
      multiPage: false,
    };

    openScanner(
      (resultBase: string | undefined) => this.scanSuccessCallback(resultBase, questionId),
      this.scanErrorCallback,
      cordovaScannerOptions
    );
  };

  handleSubmitClick = () => {
    this.setState({
      ...this.state,
      waitingForSubmit: true,
    });
    this.props.api
      .submitRecruitmentForm()
      .then(() => {
        this.setState({
          ...this.state,
          waitingForSubmit: false,
          showSubmitModal: true,
        });
      })
      .catch((_err) => {
        this.setState({
          ...this.state,
          waitingForSubmit: false,
          showSubmitModal: false,
          showMandatoryProblems: true,
        });
        alert("You must answer all the questions before you can submit");
      });
  };
  hideModal = () => {
    this.setState({
      showSubmitModal: false,
    });
  };

  render() {
    return (
      <RecruitmentFormScreen
        onPicUpload={this.handlePicUpload}
        onBackClick={this.props.onBackClick}
        recruitmentForm={this.state.recruitmentForm}
        onAnswer={this.handleAnswer}
        language={this.state.language}
        showMadatoryProblems={this.state.showMandatoryProblems}
        awaitingSubmit={this.state.waitingForSubmit}
        showModal={this.state.showSubmitModal}
        onHideModal={this.hideModal}
        onSubmit={this.handleSubmitClick}
        confirmFinish={this.props.onSubmitted}
        caregiver={this.props.caregiver}
      />
    );
  }
}
